import { computed, unref } from '@vue/composition-api';
import {
  flattenSavedSearchNewMatchesInGroupOrder,
  groupSavedSearchNewMatches,
  savedSearchDisplayGroupSections,
} from '../utils/savedSearchDisplayGroups.js';

/**
 * Primary digest list for saved-search new matches (API: saved_search_new_matches).
 * Falls back to saved_searches_new_awarded_contracts during backend rollout.
 *
 * @param {Object|null|undefined} digest - dailyDigest object
 * @returns {Array<Object>}
 */
export function resolveSavedSearchNewMatches(digest) {
  if (!digest || typeof digest !== 'object') return [];
  const raw =
    digest.saved_search_new_matches ?? digest.saved_searches_new_awarded_contracts;
  return Array.isArray(raw) ? raw : [];
}

/**
 * Normalizes saved-search match rows for the UI.
 * Flat list with `matched_searches: [{ search_id, search_name }]`.
 * Order is preserved as returned by the API.
 *
 * @param {Array} raw
 * @returns {Array<Object>}
 */
export function normalizeSavedSearchNewMatches(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];

  return raw.map((row) => ({
    ...row,
    matched_searches: Array.isArray(row?.matched_searches) ? row.matched_searches : [],
  }));
}

/**
 * Composable for saved-search new matches from the daily digest.
 *
 * @param {Object} params
 * @param {Object} params.dailyDigest - dailyDigest ref from useDailyDigest
 * @param {Object} params.loading - loading ref from useDailyDigest
 */
export function useSavedSearchNewMatches({ dailyDigest, loading }) {
  const savedSearchNewMatches = computed(() => {
    const digest = unref(dailyDigest);
    return normalizeSavedSearchNewMatches(resolveSavedSearchNewMatches(digest));
  });

  const savedSearchNewMatchesGrouped = computed(() =>
    groupSavedSearchNewMatches(savedSearchNewMatches.value)
  );

  const savedSearchNewMatchesSections = computed(() =>
    savedSearchDisplayGroupSections(savedSearchNewMatches.value)
  );

  const savedSearchNewMatchesFlatInGroupOrder = computed(() =>
    flattenSavedSearchNewMatchesInGroupOrder(savedSearchNewMatches.value)
  );

  return {
    savedSearchNewMatches,
    savedSearchNewMatchesGrouped,
    savedSearchNewMatchesSections,
    savedSearchNewMatchesFlatInGroupOrder,
    loading,
  };
}
